import { ApiClient } from '@/lib/axios/client';
import type {
  BatchMasterDataRequest,
  BatchMasterDataResult,
  CreateMasterDataRowDto,
  MasterDataImportResult,
  MasterDataListing,
  MasterDataListParams,
  MasterDataRow,
  UpdateMasterDataRowDto,
} from './types';

export class MasterDataApi {
  static async list(orgId: string, params?: MasterDataListParams): Promise<MasterDataListing> {
    const queryParams = new URLSearchParams();
    if (params?.dataset) queryParams.set('dataset', params.dataset);
    if (params?.page) queryParams.set('page', params.page.toString());
    if (params?.page_size) queryParams.set('page_size', params.page_size.toString());
    if (params?.search) queryParams.set('search', params.search);
    if (params?.from) queryParams.set('from', params.from);
    if (params?.to) queryParams.set('to', params.to);
    const query = queryParams.toString();
    return ApiClient.get<MasterDataListing>(
      `/api/v1/orgs/${orgId}/master-data${query ? `?${query}` : ''}`
    );
  }

  static async listDatasets(orgId: string): Promise<string[]> {
    return ApiClient.get<string[]>(`/api/v1/orgs/${orgId}/master-data/datasets`);
  }

  static async create(orgId: string, dto: CreateMasterDataRowDto): Promise<MasterDataRow> {
    return ApiClient.post<MasterDataRow>(`/api/v1/orgs/${orgId}/master-data`, dto);
  }

  static async update(orgId: string, rowId: number, dto: UpdateMasterDataRowDto): Promise<MasterDataRow> {
    return ApiClient.patch<MasterDataRow>(`/api/v1/orgs/${orgId}/master-data/${rowId}`, dto);
  }

  static async remove(orgId: string, rowId: number): Promise<void> {
    return ApiClient.delete(`/api/v1/orgs/${orgId}/master-data/${rowId}`);
  }

  static async importCsv(orgId: string, dataset: string, file: File): Promise<MasterDataImportResult> {
    const form = new FormData();
    form.append('dataset', dataset);
    form.append('file', file);
    return ApiClient.post<MasterDataImportResult>(
      `/api/v1/orgs/${orgId}/master-data/import`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  }

  static async batch(orgId: string, req: BatchMasterDataRequest): Promise<BatchMasterDataResult> {
    return ApiClient.post<BatchMasterDataResult>(`/api/v1/orgs/${orgId}/master-data/batch`, req);
  }

  static async exportExcel(orgId: string, params?: Pick<MasterDataListParams, 'dataset' | 'search' | 'from' | 'to'>): Promise<void> {
    const qs = new URLSearchParams();
    if (params?.dataset) qs.set('dataset', params.dataset);
    if (params?.search)  qs.set('search',  params.search);
    if (params?.from)    qs.set('from',    params.from);
    if (params?.to)      qs.set('to',      params.to);
    const res = await fetch(`/api/v1/orgs/${orgId}/master-data/export?${qs}`, { credentials: 'include' });
    if (!res.ok) throw new Error('Export failed');
    const blob = await res.blob();
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `master-data${params?.dataset ? `-${params.dataset}` : ''}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  }
}
